import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ArrowLeft, BrainCircuit, Trophy, ChevronRight, Zap, Target, 
  Clock, Calendar, AlertCircle, RefreshCw, Sparkles, ExternalLink, X,
  Search, Filter, Loader2
} from 'lucide-react';
import { supabase } from '../../services/authService';
import { MatchDetail, Language } from '../../types';
import { t } from '../../i18n';
import { FullPageLoader } from '../Common/FullPageLoader';
import { getFromCache, saveToCache } from '../../services/storageService';
import { getTeamName } from '../../services/teamService';
import { getLeagueName, getLeagueNameFromAny } from '../../services/leagueService';

interface MyPredictionsViewProps {
  userId: string;
  lang: Language;
  onBack: () => void;
}

interface PredictionItem {
  id: string;
  post_id: string;
  user_id: string;
  predicted_winner: string;
  predicted_score: string;
  win_probability: string;
  analysis_reason: string;
  analysis_reason_ko: string;
  // analysis_reason_ja?: string;
  // analysis_reason_es?: string;
  // analysis_reason_fr?: string;
  home_team_id?: string;
  away_team_id?: string;
  home_team_name?: string;
  away_team_name?: string;
  league_id?: string;
  league_name?: string;
  created_at: string;
  matchDetail?: MatchDetail;
}

export const MyPredictionsView: React.FC<MyPredictionsViewProps> = ({ userId, lang, onBack }) => {
  const [predictions, setPredictions] = useState<PredictionItem[]>([]);
  const [filteredPredictions, setFilteredPredictions] = useState<PredictionItem[]>([]);
  const [stats, setStats] = useState({ total: 0, correct: 0, pending: 0, accuracy: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedPrediction, setSelectedPrediction] = useState<PredictionItem | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedPrediction) {
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [selectedPrediction]);

  // Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('all'); // all, today, week, month

  const loadPredictions = useCallback(async (isInitial: boolean) => {
    const currentPage = isInitial ? 0 : page;
    
    if (isInitial) {
      setLoading(true);
      setPredictions([]);
      setPage(0);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const from = currentPage * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error } = await supabase
        .from('match_predictions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      if (data) {
        if (isInitial) {
          setPredictions(data);
        } else {
          setPredictions(prev => [...prev, ...data]);
        }
        
        setHasMore(data.length === PAGE_SIZE);
        setPage(currentPage + 1);
      }
    } catch (e) {
      console.error('Error loading predictions:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, page]);

  useEffect(() => {
    loadPredictions(true);
  }, [userId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore && searchQuery === '' && selectedLeague === 'all' && selectedPeriod === 'all') {
          loadPredictions(false);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, loadingMore, loadPredictions, searchQuery, selectedLeague, selectedPeriod]);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [predictions, searchQuery, selectedLeague, selectedPeriod]);

  const calculateStats = () => {
    setStats({
      total: predictions.length,
      correct: 0,
      pending: predictions.length,
      accuracy: 0
    });
  };

  const applyFilters = () => {
    let result = [...predictions];

    // Search filter (Team names)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(pred => {
        const homeName = getTeamName(pred.home_team_id || '', lang, pred.home_team_name || '').toLowerCase();
        const awayName = getTeamName(pred.away_team_id || '', lang, pred.away_team_name || '').toLowerCase();
        const homeOriginal = (pred.home_team_name || '').toLowerCase();
        const awayOriginal = (pred.away_team_name || '').toLowerCase();
        
        return homeName.includes(q) || awayName.includes(q) || homeOriginal.includes(q) || awayOriginal.includes(q);
      });
    }

    // League filter
    if (selectedLeague !== 'all') {
      result = result.filter(pred => pred.league_name === selectedLeague);
    }

    // Period filter
    if (selectedPeriod !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      result = result.filter(pred => {
        const predDate = new Date(pred.created_at);
        if (selectedPeriod === 'today') {
          return predDate >= today;
        } else if (selectedPeriod === 'week') {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return predDate >= weekAgo;
        } else if (selectedPeriod === 'month') {
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return predDate >= monthAgo;
        }
        return true;
      });
    }

    setFilteredPredictions(result);
  };

  if (loading) return (
    <div className="py-32 flex flex-col items-center justify-center">
      <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
      <span className="mt-4 text-xs font-bold text-slate-400">
        {t(lang, 'loading')}
      </span>
    </div>
  );


  const handleCloseModal = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setSelectedPrediction(null);
    }, 500);
  };

  const renderPredictionModal = () => {
    if (!selectedPrediction) return null;
    const winProb = Math.round(Number(selectedPrediction.win_probability) || 50);

    const homeId = selectedPrediction.home_team_id || '';
    const awayId = selectedPrediction.away_team_id || '';
    const isHomeDominant = selectedPrediction.predicted_winner === selectedPrediction.home_team_name;

    const topId = isHomeDominant ? homeId : awayId;
    const bottomId = isHomeDominant ? awayId : homeId;

    const topDisplayName = getTeamName(String(topId), lang, (isHomeDominant ? selectedPrediction.home_team_name : selectedPrediction.away_team_name) || '');
    const bottomDisplayName = getTeamName(String(bottomId), lang, (isHomeDominant ? selectedPrediction.away_team_name : selectedPrediction.home_team_name) || '');

    return (
      <div className={`fixed inset-0 z-[300] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
        <div className="absolute inset-0 bg-slate-950/20 backdrop-blur-[2px]" onClick={handleCloseModal}></div>

        <div 
          className={`relative bg-white w-full max-w-lg md:rounded-[40px] rounded-t-[40px] overflow-hidden h-[85vh] md:h-auto md:max-h-[90vh] flex flex-col transition-all duration-500 ease-[0.32,0.72,0,1] border-t md:border border-slate-100 ${isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}
        >
          {/* Modal Header */}
          <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between bg-white sticky top-0 z-20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-900">
                <BrainCircuit size={22} />
              </div>
              <div>
                <h4 className="text-base font-black text-slate-900 uppercase tracking-tight">{t(lang, 'aiTacticalInsight')}</h4>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t(lang, 'detailedAnalysis')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleCloseModal}
                className="w-10 h-10 rounded-full bg-slate-50 text-slate-400 hover:text-slate-900 transition-all flex items-center justify-center"
              >
                <X size={20} strokeWidth={3} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 custom-scrollbar bg-white">
            <div className="p-8 space-y-8">
                <>
                  {/* Match Header */}
                  <div className="text-center space-y-6">
                    <span className="inline-block px-3 py-1 rounded-full bg-slate-50 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] border border-slate-100">
                      {selectedPrediction.league_id ? getLeagueName(Number(selectedPrediction.league_id), lang, selectedPrediction.league_name) : getLeagueNameFromAny(selectedPrediction.league_name, lang, 'Match Intel')}
                    </span>
                    <div className="flex items-center justify-between gap-4 px-2">
                      <div className="flex flex-col items-center gap-3 flex-1 min-w-0">
                        <div className="w-20 h-20 p-4 bg-slate-50 rounded-[32px] flex items-center justify-center border border-slate-100 shadow-sm transition-transform hover:scale-105">
                          <img src={`https://media.api-sports.io/football/teams/${homeId}.png`} alt="" className="w-full h-full object-contain" onError={(e) => e.currentTarget.src = `https://ui-avatars.com/api/?name=${selectedPrediction.home_team_name}&background=random`} />
                        </div>
                        <span className="text-xs font-black text-slate-900 uppercase tracking-tight text-center truncate w-full">{selectedPrediction.home_team_name ? getTeamName(String(homeId), lang, selectedPrediction.home_team_name) : getTeamName(String(homeId), lang)}</span>
                      </div>
                      
                      <div className="flex flex-col items-center justify-center min-w-[80px]">
                        <span className="text-3xl font-black text-slate-900 tracking-widest tabular-nums">{selectedPrediction.predicted_score}</span>
                      </div>

                      <div className="flex flex-col items-center gap-3 flex-1 min-w-0">
                        <div className="w-20 h-20 p-4 bg-slate-50 rounded-[32px] flex items-center justify-center border border-slate-100 shadow-sm transition-transform hover:scale-105">
                          <img src={`https://media.api-sports.io/football/teams/${awayId}.png`} alt="" className="w-full h-full object-contain" onError={(e) => e.currentTarget.src = `https://ui-avatars.com/api/?name=${selectedPrediction.away_team_name}&background=random`} />
                        </div>
                        <span className="text-xs font-black text-slate-900 uppercase tracking-tight text-center truncate w-full">{selectedPrediction.away_team_name ? getTeamName(String(awayId), lang, selectedPrediction.away_team_name) : getTeamName(String(awayId), lang)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Analysis Content */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-4 bg-rose-500 rounded-full" />
                      <h5 className="text-[11px] font-black text-slate-900 uppercase tracking-widest">
                        {t(lang, 'tacticalInsight')}
                      </h5>
                    </div>
                    <div className="text-slate-700 text-[15px] leading-[1.8] font-medium whitespace-pre-wrap bg-slate-50/50 border border-slate-100 rounded-[32px] p-7">
                      {lang === 'ko' 
                        ? selectedPrediction.analysis_reason_ko 
                        // : lang === 'ja'
                        // ? selectedPrediction.analysis_reason_ja || selectedPrediction.analysis_reason
                        // : lang === 'es'
                        // ? selectedPrediction.analysis_reason_es || selectedPrediction.analysis_reason
                        // : lang === 'fr'
                        // ? selectedPrediction.analysis_reason_fr || selectedPrediction.analysis_reason
                        : selectedPrediction.analysis_reason}
                    </div>
                  </div>

                  <button 
                    onClick={() => window.navigateTo(`/game/${selectedPrediction.post_id}`)}
                    className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold text-xs shadow-sm hover:bg-slate-800 active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    {t(lang, 'viewDetails')}
                    <ChevronRight size={14} />
                  </button>
                </>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const leagues = Array.from(new Set(predictions.map(p => p.league_name).filter(Boolean))) as string[];

  return (
    <div className="p-6 pb-32">
      {renderPredictionModal()}
      {predictions.length === 0 ? (
        <div className="py-32 flex flex-col items-center justify-center text-center px-10">
          <div className="w-20 h-20 bg-slate-50 rounded-[30%] flex items-center justify-center text-slate-200 mb-6">
            <BrainCircuit size={40} />
          </div>
          <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-2">
            {t(lang, 'noPredictionsYet')}
          </h3>
          <p className="text-xs font-medium text-slate-400 leading-relaxed uppercase tracking-wide">
            {t(lang, 'analyzeMatchesDesc')}
          </p>
          <button 
            onClick={() => window.navigateTo('/games')}
            className="mt-8 px-8 py-3 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg active:scale-95"
          >
            {t(lang, 'exploreMatches')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">

          {filteredPredictions.length === 0 && !loading ? (
            <div className="py-20 text-center bg-slate-50 rounded-[32px] border-2 border-dashed border-slate-100">
              <Search size={32} className="mx-auto text-slate-200 mb-3" />
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
                {t(lang, 'noResultsFound')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredPredictions.map((pred) => {
                return (
                  <button
                  key={pred.id}
                  onClick={() => setSelectedPrediction(pred)}
                  className="w-full bg-white border border-gray-100 p-4 md:p-5 rounded-[28px] md:hover:border-rose-200 transition-all text-left group shadow-sm md:hover:shadow-md flex items-center gap-3 md:gap-4"
                >
                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-300 md:group-hover:text-rose-500 md:group-hover:bg-rose-50 transition-colors shrink-0 overflow-hidden border border-gray-50">
                  <div className="flex -space-x-4">
                    <img src={`https://media.api-sports.io/football/teams/${pred.home_team_id}.png`} className="w-8 h-8 object-contain z-10" alt="" onError={(e) => e.currentTarget.src = `https://ui-avatars.com/api/?name=${pred.home_team_name}&background=random`} />
                    <img src={`https://media.api-sports.io/football/teams/${pred.away_team_id}.png`} className="w-8 h-8 object-contain" alt="" onError={(e) => e.currentTarget.src = `https://ui-avatars.com/api/?name=${pred.away_team_name}&background=random`} />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] max-w-[100px] md:max-w-[150px] truncate font-black text-rose-500 uppercase tracking-widest">
                        {pred.league_id ? getLeagueName(Number(pred.league_id), lang, pred.league_name) : getLeagueNameFromAny(pred.league_name, lang, 'Match Intel')}
                      </span>
                      <span className="text-slate-300 text-[8px]">·</span>
                      <span className="text-slate-400 text-[8px] font-bold uppercase">
                        {new Date(pred.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <h4 className="text-sm font-black text-slate-900 uppercase tracking-tight truncate mb-1">
                    {`${pred.home_team_name ? getTeamName(pred.home_team_id || '', lang, pred.home_team_name) : getTeamName(pred.home_team_id || '', lang)} vs ${pred.away_team_name ? getTeamName(pred.away_team_id || '', lang, pred.away_team_name) : getTeamName(pred.away_team_id || '', lang)}`}
                  </h4>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Zap size={10} className="text-amber-500 fill-amber-500" />
                      <span className="text-[10px] font-black text-slate-600 truncate max-w-[80px] md:max-w-none">
                        {pred.predicted_winner === pred.home_team_name 
                          ? (pred.home_team_name ? getTeamName(pred.home_team_id || '', lang, pred.home_team_name) : getTeamName(pred.home_team_id || '', lang))
                          : pred.predicted_winner === pred.away_team_name
                          ? (pred.away_team_name ? getTeamName(pred.away_team_id || '', lang, pred.away_team_name) : getTeamName(pred.away_team_id || '', lang))
                          : pred.predicted_winner}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 border-l border-gray-100 pl-3">
                      <Target size={10} className="text-indigo-500" />
                      <span className="text-[10px] font-black text-slate-600">{pred.predicted_score}</span>
                    </div>
                  </div>
                </div>

                  <ChevronRight size={18} className="text-slate-200 md:group-hover:text-rose-400 md:group-hover:translate-x-1 transition-all shrink-0" />
                </button>
                );
              })}
            </div>
          )}

          {/* Infinite Scroll Loader */}
          <div ref={observerTarget} className="h-20 flex items-center justify-center mt-4">
            {loadingMore && (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 text-rose-500 animate-spin" />
                <span className="text-xs font-medium text-slate-400">
                  {t(lang, 'loadingMore')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
